import { useState } from "react";
import { api } from "@/api/client";
import { Check, X, GitCommit, Sparkles, User } from "lucide-react";
import { toast } from "sonner";

export function ApprovalCard({ approval, onDecided }) {
  const [busy, setBusy] = useState(false);

  const decide = async (approve) => {
    setBusy(true);
    try {
      const r = await api.post(`/approvals/${approval.id}/decide`, { approve });
      if (r.data.ok) {
        if (r.data.commit && !r.data.commit.ok && r.data.commit.conflict) {
          toast.error("Conflict detected in Notion: no silent overwrite.");
        } else {
          toast.success(approve ? "Approved & committed to Notion" : "Rejected");
        }
        onDecided?.(r.data);
      } else if (r.data.error) toast.error(r.data.error);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to decide");
    } finally { setBusy(false); }
  };

  const diff = approval.diff || {};
  const isAI = approval.source === "ai";

  return (
    <div data-testid={`approval-card-${approval.id}`} className="rounded-lg border border-[#E7E5E4] bg-[#FFFFFF] overflow-hidden">
      <header className="flex items-center justify-between border-b border-[#E7E5E4] bg-[#F5F5F4] px-4 py-2.5">
        <div className="flex items-center gap-2 text-xs">
          <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 ${isAI ? "bg-[#FFC817]/10 text-[#B45309]" : "bg-black/5 text-[#1C1C1E]"}`}>
            {isAI ? <Sparkles className="h-3 w-3" /> : <User className="h-3 w-3" />}
            {isAI ? "AI proposal" : "User proposal"}
          </span>
          <span className="text-[#A8A29E] font-mono">{approval.entity}/{(approval.record_id || "new").slice(0,8)}</span>
          <span className="text-[#A8A29E]">by {approval.proposed_by}</span>
        </div>
        <span className="text-[10px] font-mono text-[#A8A29E]">{approval.created_at?.slice(0, 19)}</span>
      </header>
      <div className="p-4 space-y-1.5 font-mono text-xs">
        {Object.entries(diff).map(([k, v]) => (
          <div key={k} className="space-y-1">
            <div className="text-[#A8A29E] uppercase tracking-wider text-[10px]">{k}</div>
            <div className="pl-2 flex flex-col gap-0.5">
              <div className="text-red-600 line-through">- {String(v.before ?? "∅")}</div>
              <div className="text-emerald-600">+ {String(v.after ?? "∅")}</div>
            </div>
          </div>
        ))}
      </div>
      <footer className="flex justify-end gap-2 border-t border-[#E7E5E4] px-4 py-2.5">
        <button
          data-testid={`approval-reject-${approval.id}`}
          onClick={() => decide(false)} disabled={busy}
          className="inline-flex items-center gap-1 rounded-md border border-[#E7E5E4] bg-transparent px-3 py-1.5 text-xs text-[#57534E] hover:bg-[#F5F5F4] hover:text-[#1C1C1E] disabled:opacity-50"
        >
          <X className="h-3.5 w-3.5" /> Reject
        </button>
        <button
          data-testid={`approval-approve-${approval.id}`}
          onClick={() => decide(true)} disabled={busy}
          className="inline-flex items-center gap-1 rounded-md bg-[#FFC817] px-3 py-1.5 text-xs font-medium text-[#1C1C1E] hover:bg-[#E6B415] disabled:opacity-50"
        >
          <Check className="h-3.5 w-3.5" /> Approve & Commit
        </button>
      </footer>
    </div>
  );
}
